// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useRef } from 'react';
import { Button, AppIcon, StatusBanner, resizeImage } from '@loa/ui-components';
import { advisorApi } from '../api/advisorApi';
import type { AdvisorProfile } from '../types/advisor';

type AdvisorPhotoUploadProps = Readonly<{
  currentPhotoUrl?: string;
  advisorId: string;
  onPhotoUpdated: (profile: AdvisorProfile) => void;
  compact?: boolean;
}>;

export function AdvisorPhotoUpload({
  currentPhotoUrl,
  advisorId,
  onPhotoUpdated,
  compact = false
}: AdvisorPhotoUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(currentPhotoUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPEG or PNG)');
      return;
    }

    // Validate file type (JPEG or PNG only)
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setError('Only JPEG and PNG images are supported');
      return;
    }

    try {
      setUploading(true);

      // Resize image client-side before upload
      const resizedBlob = await resizeImage(file, 600);

      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target?.result as string;
        setPreview(base64Data);

        try {
          const updatedProfile = await advisorApi.updatePhoto(advisorId, base64Data);
          onPhotoUpdated(updatedProfile);
          setError(null);
        } catch (err: unknown) {
          const error = err as { response?: { data?: { message?: string } } };
          console.error('Failed to upload photo:', err);
          setError(error.response?.data?.message || 'Failed to upload photo. Please try again.');
          setPreview(currentPhotoUrl || null);
        } finally {
          setUploading(false);
        }
      };

      reader.onerror = () => {
        setError('Failed to read file. Please try again.');
        setUploading(false);
      };

      reader.readAsDataURL(resizedBlob);
    } catch {
      setError('Failed to process image. Please try again.');
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!globalThis.confirm('Are you sure you want to remove your profile photo?')) {
      return;
    }

    try {
      setUploading(true);
      const updatedProfile = await advisorApi.removePhoto(advisorId);
      onPhotoUpdated(updatedProfile);
      setPreview(null);
      setError(null);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Failed to remove photo:', err);
      setError(error.response?.data?.message || 'Failed to remove photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  if (compact) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {preview ? (
          <img
            src={preview}
            alt="Profile"
            style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }}
          />
        ) : (
          <AppIcon name="user" size={32} color="grey" />
        )}
      </div>
    );
  }

  return (
    <>
      <p className="settings-photo__description">
        Upload a professional photo of yourself. This will be visible to clients.
      </p>

      <div className="settings-photo-row">
        {preview ? (
          <img
            src={preview}
            alt="Your profile"
            className="settings-photo-avatar"
          />
        ) : (
          <div className="settings-photo-placeholder">
            <AppIcon name="user" size={36} color="grey" />
          </div>
        )}

        <Button
          variant="filled"
          size="md"
          onClick={handleUploadClick}
          loading={uploading}
          disabled={uploading}
          iconLeft={<AppIcon name="upload cloud arrow" size={16} />}
        >
          Change photo
        </Button>

        {preview && (
          <Button
            variant="outlined"
            size="md"
            onClick={handleRemovePhoto}
            loading={uploading}
            disabled={uploading}
            iconLeft={<AppIcon name="trash" size={16} />}
          >
            Remove photo
          </Button>
        )}
      </div>

      <p className="settings-photo__formats">
        Accepted formats: JPEG, PNG &bull; Images are automatically resized
      </p>

      {error && (
        <StatusBanner
          variant="error"
          message={error}
          visible
          position="inline"
          dismissible={false}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
    </>
  );
}
